import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight, Calendar, Mail, Linkedin, Link2 } from 'lucide-react';
import Navigation from '../components/home/Navigation';
import Footer from '../components/home/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const articles = [
  {
    slug: 'july-2026',
    volume: 'Volume 1',
    issue: 'Issue 1',
    date: 'July 2026',
    title: 'AI Is Creating Value Faster. Execution and Oversight Are Now the Real Test.',
    subtitle: 'A monthly signal for leaders navigating capital, intelligence, and execution.',
    heroImage: `${API}/resources/newsletter-img-2.jpg`,
    heroAlt: 'Healthcare professional using medical technology equipment',
    heroCaption: 'MedTech M&A remains active into midyear, reinforcing continued pressure on leadership teams to align growth strategy, capital, and execution discipline.',
    heroCaptionSource: { label: 'PwC', url: 'https://www.pwc.com/us/en/industries/health-industries/library/medtech-deals-outlook.html' },
    component: 'july2026',
  },
  {
    slug: 'pharma-informer',
    volume: 'Volume 1',
    issue: 'Issue 2',
    date: 'July 2026',
    title: 'Greater Clarity for Customer Forecasting as a Global Healthcare Leader Launches a New Product',
    subtitle: 'How ASEMAP™ predictive behavioral science delivered launch intelligence for a global RA product across ten countries and over 1,000 healthcare providers.',
    heroImage: `${API}/resources/newsletter-pharma-hero.jpg`,
    heroAlt: 'Pharmaceutical research and analytics',
    heroCaption: 'Predictive intelligence for pharmaceutical product launches — from market evaluation to physician prescribing behavior analysis.',
    heroCaptionSource: { label: 'APIG / OSG Analytics', url: 'https://osganalytics.com' },
    badge: 'APIG / OSG Analytics',
    pdfDownload: `${API}/resources/pharma-informer-issue-9.pdf`,
    component: 'pharmaInformer',
  },
  {
    slug: 'patient-mind',
    volume: 'Volume 1',
    issue: 'Issue 3',
    date: 'July 2026',
    title: 'Inside the Patient Mind: Why Accurate Medical Advice Doesn\'t Always Translate Into Action',
    subtitle: 'The critical treatment decisions patients make at the kitchen table — and why behavioral psychology, not more information, holds the key to adherence.',
    heroImage: `${API}/resources/newsletter-patient-mind-hero.jpg`,
    heroAlt: 'Doctor-patient consultation in healthcare setting',
    heroCaption: 'Critical treatment decisions are rarely finalized within the physician\'s office — patients evaluate options at home, influenced by fear, doubt, and cost.',
    heroCaptionSource: { label: 'The Strategix / OSG Analytics', url: 'https://osganalytics.com' },
    badge: 'APIG / OSG Analytics',
    pdfDownload: `${API}/resources/patient-mind-newsletter.pdf`,
    component: 'patientMind',
  },
  {
    slug: 'breakthrough-mentoring',
    volume: 'Volume 1',
    issue: 'Issue 4',
    date: 'July 2026',
    title: 'Breakthrough Mentoring: The Essence of Leadership Is Influence',
    subtitle: 'Forest T. Harper Jr. on why the leaders who matter most are the ones who make other leaders. A conversation about mentoring, communication, and the discipline of influence.',
    heroImage: `${API}/resources/newsletter-img-3.jpg`,
    heroAlt: 'Forest T. Harper Jr.',
    heroCaption: 'Forest T. Harper Jr., Harper & Cook Consulting, on leadership influence through mentoring.',
    heroCaptionSource: { label: 'APIG / Harper Cook Consulting', url: 'https://harpercookconsultingllc.com' },
    badge: 'APIG / Harper Cook Consulting',
    component: 'breakthroughMentoring',
  },
  {
    slug: 'strategic-intelligence-for-leaders',
    volume: 'Volume 1',
    issue: 'Issue 5',
    date: 'August 2026',
    title: 'Strategic Intelligence for Leaders: Turning Market Complexity Into Clarity',
    subtitle: 'Erania Brackett on how leaders can combine data-driven insights with real-world execution to make confident decisions that drive growth and transformation.',
    heroImage: 'https://customer-assets.emergentagent.com/job_three-superpowers/artifacts/pvn6eazu_Erania%20Head%20Shot_best.jpg',
    heroAlt: 'Erania Brackett, Founder and Principal of Brackett Advisory',
    heroCaption: 'Erania Brackett discusses predictive intelligence, brand strategy, and customer experience design for executive decision-making.',
    heroCaptionSource: { label: 'XRaised / Brackett Partners', url: 'https://brackett.partners' },
    badge: 'APIG / Brackett Partners',
    component: 'strategicIntelligence',
  },
  {
    slug: 'above-the-clouds',
    volume: 'Volume 1',
    issue: 'Issue 6',
    date: 'August 2026',
    title: 'Above the Clouds: Your AI Is Only as Good as Its Data',
    subtitle: 'Why companies racing to deploy AI without unique data end up looking exactly like their competitors. Dr. R. Sukumar on the pitfalls of averages, the limits of automation, and why the market doesn\'t want lukewarm tea.',
    heroImage: `${API}/resources/newsletter-above-clouds-hero.jpg`,
    heroAlt: 'AI and data analytics visualization',
    heroCaption: 'AI tools relying on the same public data produce the same generic outputs. Unique data is the competitive edge.',
    heroCaptionSource: { label: 'APIG / OSG Analytics', url: 'https://osganalytics.com' },
    badge: 'APIG / OSG Analytics',
    pdfDownload: `${API}/resources/above-the-clouds-newsletter.pdf`,
    component: 'aboveClouds',
  },
];

function ShareButtons({ article, position = 'top' }) {
  const shareUrl = `https://predictiveintelligencegrowthalliance.com/api/share/newsletter/${article.slug}`;
  const title = article.title;
  const body = `${article.title}\n\n${article.subtitle}\n\nRead the full brief from APIG — The Alliance for Predictive Intelligence & Growth:\n${shareUrl}`;

  const emailHref = `mailto:?subject=${encodeURIComponent(`APIG Brief: ${title}`)}&body=${encodeURIComponent(body)}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      const el = document.getElementById(`copy-confirm-${position}-${article.slug}`);
      if (el) { el.textContent = 'Copied'; setTimeout(() => { el.textContent = 'Copy link'; }, 2000); }
    });
  };

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid={`share-buttons-${position}`}>
      <span className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Share</span>
      <a
        href={emailHref}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#334155] border border-[#E2E8F0] hover:border-[#D97706] hover:text-[#D97706] transition-colors"
        data-testid="share-email"
      >
        <Mail className="w-3.5 h-3.5" />
        Email
      </a>
      <a
        href={linkedinHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#334155] border border-[#E2E8F0] hover:border-[#0A66C2] hover:text-[#0A66C2] transition-colors"
        data-testid="share-linkedin"
      >
        <Linkedin className="w-3.5 h-3.5" />
        LinkedIn
      </a>
      <button
        onClick={copyLink}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#334155] border border-[#E2E8F0] hover:border-[#D97706] hover:text-[#D97706] transition-colors cursor-pointer bg-transparent"
        data-testid="share-copy"
      >
        <Link2 className="w-3.5 h-3.5" />
        <span id={`copy-confirm-${position}-${article.slug}`}>Copy link</span>
      </button>
    </div>
  );
}

function ArticlePage({ article }) {
  return (
    <>
      <Helmet>
        <title>{`${article.title} | The APIG Brief`}</title>
        <meta name="description" content={`The APIG Brief ${article.date}: ${article.title}. ${article.subtitle}`} />
        <link rel="canonical" href={`https://predictiveintelligencegrowthalliance.com/newsletter/${article.slug}`} />
      </Helmet>

      {/* Article Header */}
      <section className="pt-32 pb-8 bg-white">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <span className="text-xs font-medium uppercase tracking-wider text-[#D97706]">{article.badge || 'The APIG Brief'}</span>
              <span className="text-[#CBD5E1]">|</span>
              <span className="text-xs text-[#64748B]">{article.volume} · {article.issue}</span>
              <span className="text-[#CBD5E1]">|</span>
              <span className="text-xs text-[#64748B]">{article.date}</span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] text-[#0B1A2B] leading-tight mb-6" data-testid="article-headline">
              {article.title}
            </h1>
            <p className="text-[#475569] text-base leading-relaxed">
              {article.subtitle}
            </p>
            {article.pdfDownload && (
              <a href={article.pdfDownload} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-[#0B1A2B] hover:text-[#D97706] transition-colors border border-[#E2E8F0] px-4 py-2" data-testid="pdf-download-link">
                <ArrowUpRight className="w-4 h-4" />
                Download Original PDF
              </a>
            )}
            <div className="mt-5">
              <ShareButtons article={article} position="top" />
            </div>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="pb-8 bg-white">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto">
            <div className="w-full overflow-hidden">
              <img src={article.heroImage} alt={article.heroAlt} className="w-full h-auto" loading="lazy" />
            </div>
            <p className="text-xs text-[#94A3B8] mt-3 leading-relaxed">
              {article.heroCaption}{' '}
              <a href={article.heroCaptionSource.url} target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">
                Source: {article.heroCaptionSource.label}
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <section className="pb-16 bg-white">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto">
            <div className="article-body space-y-6">
              {article.component === 'pharmaInformer' ? <PharmaInformerBody /> : article.component === 'patientMind' ? <PatientMindBody /> : article.component === 'breakthroughMentoring' ? <BreakthroughMentoringBody /> : article.component === 'strategicIntelligence' ? <StrategicIntelligenceBody /> : article.component === 'aboveClouds' ? <AboveCloudsBody /> : <July2026Body />}
            </div>
            <div className="mt-10 pt-6 border-t border-[#E2E8F0]">
              <ShareButtons article={article} position="bottom" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
function July2026Body() {
  return (
    <>
              <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
                You sit at a table where the decisions matter. A board table, a deal room, a founder's kitchen counter at midnight. We built the APIG Brief to make those conversations sharper. Not more data. Better judgment.
              </p>

              <h2 className="font-serif text-2xl text-[#0B1A2B] pt-4">This Month's Story</h2>

              <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
                FTI Consulting surveyed 555 senior private equity leaders across 14 countries for its <em>2026 Value Creation Index</em>. The headline number: 66% now report AI-driven benefits within 12 months. A year ago, that figure was 34%. Meanwhile, M&A jumped from the bottom of the value-lever rankings in 2025 to the top in 2026. More than half said they beat their M&A business case.{' '}
                <a href="https://www.fticonsulting.com/about/newsroom/press-releases/ai-speeds-up-returns-in-private-equity-as-ma-becomes-top-value-generator-for-firms" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">FTI Consulting</a>
              </p>

              <div className="grid grid-cols-3 gap-4 py-6" data-testid="newsletter-stats">
                {[
                  { value: '66%', label: 'Report AI benefits within 12 months' },
                  { value: '51%', label: 'Beat their M&A business case' },
                  { value: '31%', label: 'Call their AI implementation efficient' },
                ].map((stat, i) => (
                  <div key={i} className="bg-[#0B1A2B] p-5 text-center">
                    <div className="font-serif text-2xl sm:text-3xl font-semibold text-[#D97706] mb-1">{stat.value}</div>
                    <div className="text-[10px] sm:text-xs text-[#94A3B8] leading-snug">{stat.label}</div>
                  </div>
                ))}
              </div>

              <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
                But here is what the headline misses. Only 31% of firms call their AI implementation efficient. Only 35% say the same about M&A integration. The returns are real. The execution is still messy. And the distance between disciplined operators and everyone else is growing fast.{' '}
                <a href="https://www.fticonsulting.com/about/newsroom/press-releases/ai-speeds-up-returns-in-private-equity-as-ma-becomes-top-value-generator-for-firms" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">FTI Consulting</a>
              </p>

              <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
                Then came a <em>Corporate Board Member</em> briefing that sharpened the picture further. Boards are now expected to show AI literacy, document oversight frameworks, and explain where AI runs in their organizations, how it's governed, and what happens when something goes wrong. Directors who cannot answer those questions clearly are exposed. The bar moved.{' '}
                <a href="https://boardmember.com/the-boards-new-ai-mandate-from-compliance-to-data-governance/" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">Corporate Board Member</a>
              </p>

              <p className="text-[#334155] leading-[1.8] text-[0.9375rem] font-medium">
                So the question for leadership teams is no longer "Should we invest in AI?" It's whether capital, governance, and execution are tight enough to turn opportunity into results before the window closes.
              </p>

              <h2 className="font-serif text-2xl text-[#0B1A2B] pt-4">An APIG Perspective</h2>

              <div className="border-l-2 border-[#D97706] pl-6 space-y-4">
                <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
                  <strong>Speed and governance are not trade-offs anymore.</strong> The old playbook said move fast or move carefully. Pick one. That's over. In this market, the firms that govern well are the ones that move fastest. Rigor is the accelerator now.
                </p>
                <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
                  <strong>The execution gap is really a capital allocation gap.</strong> When two-thirds of firms can't call their AI or M&A work efficient, the problem isn't missing strategy. It's that funding, attention, governance, and ownership aren't lined up. The money is there. The alignment isn't.
                </p>
                <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
                  <strong>Boards need better questions.</strong> Not "What is our AI strategy?" Try these instead: Where is AI deployed today? Is the data behind it clean and decision-ready? Who owns performance monitoring? Who catches bias and drift? If an activist showed up tomorrow asking for evidence of oversight, could you answer in one meeting?
                </p>
              </div>

              <h2 className="font-serif text-2xl text-[#0B1A2B] pt-8">More on the APIG Partner Firms</h2>

              <div className="space-y-6">
                {[
                  { name: 'Harper Cook Consulting', person: 'Forest T. Harper Jr.', image: `${API}/resources/newsletter-img-3.jpg`, desc: 'C-suite and board advisory, deal generation, governance transformation, and aligning capital, talent, and institutional resources to funded execution.', url: 'https://www.harpercookconsultingllc.com/' },
                  { name: 'Brackett Partners', person: 'Erania Brackett', image: `${API}/resources/newsletter-img-4.jpg`, desc: 'Strategic advisory with AI-powered predictive intelligence, plus brand, marketing, and customer experience capabilities through a Diagnose, Direct, and Deliver model.', url: 'https://brackett.partners/' },
                  { name: 'OSG Analytics', person: 'Dr. R. Sukumar', image: `${API}/resources/newsletter-img-5.jpg`, desc: 'ASEMAP behavioral science, customer journey mapping, data platforms, product R&D intelligence, and digital transformation across growth sectors.', url: 'https://osganalytics.com/' },
                ].map((firm, i) => (
                  <div key={i} className="flex gap-5 items-start p-5 bg-[#F8FAFC] border border-[#E2E8F0]" data-testid={`newsletter-firm-${i}`}>
                    <img src={firm.image} alt={firm.person} className="w-20 h-20 object-cover rounded-full flex-shrink-0" loading="lazy" />
                    <div>
                      <h3 className="font-serif text-lg text-[#0B1A2B] mb-0.5">{firm.name}</h3>
                      <p className="text-xs text-[#D97706] font-medium mb-2">{firm.person}</p>
                      <p className="text-[#475569] text-sm leading-relaxed mb-2">{firm.desc}</p>
                      <a href={firm.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#0B1A2B] hover:text-[#D97706] transition-colors inline-flex items-center gap-1">
                        {firm.url.replace('https://www.', '').replace('https://', '').replace(/\/$/, '')} <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="font-serif text-2xl text-[#0B1A2B] pt-8">One Date for Your Calendar</h2>
              <div className="p-6 bg-[#0B1A2B] text-white">
                <div className="flex items-start gap-4">
                  <Calendar className="w-6 h-6 text-[#D97706] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-serif text-lg mb-2">The 26th Annual TechBridge Digital Ball 2026</h3>
                    <p className="text-sm text-[#CBD5E1] leading-relaxed mb-3">Saturday, August 22, 2026 · Signia by Hilton Atlanta</p>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">
                      APIG is bringing <a href="https://www.aishabowe.com/" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">Aisha Bowe</a> as host. She went from NASA rocket engineer to founder. Her company <a href="https://stemlingo.com/" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">LINGO</a> builds hands-on STEM kits that have reached more than 10,000 students, including space-themed projects like <a href="https://stemlingo.com/products/countdown-to-launch" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">Countdown to Launch</a> and <a href="https://stemlingo.com/products/cubesat" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">Earth Observer</a>.
                    </p>
                    <div className="flex gap-4 mt-4">
                      <a href="https://techbridge.org/events/digital-ball/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">TechBridge</a>
                      <a href="https://www.prnewswire.com/news-releases/lingo-releases-space-themed-stem-kits-as-founder-prepares-for-historic-all-female-blue-origin-flight-302401334.html" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">LINGO release</a>
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="font-serif text-2xl text-[#0B1A2B] pt-8">Also Worth Your Attention</h2>
              <ul className="space-y-3">
                <li className="text-[#334155] text-[0.9375rem] leading-[1.8]">MedTech M&A keeps running hot. PwC's midyear outlook says 2026 will extend the momentum from a decade-high 2025. <a href="https://www.pwc.com/us/en/industries/health-industries/library/medtech-deals-outlook.html" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">PwC</a></li>
                <li className="text-[#334155] text-[0.9375rem] leading-[1.8]">68% of CEOs plan to increase AI spending over the next two years. Only 18% think their technology is actually ready. That gap tells you everything. <a href="https://group.ntt/en/topics/2026/01/14/wsj_survey.html" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">NTT</a></li>
                <li className="text-[#334155] text-[0.9375rem] leading-[1.8]">The Conference Board's C-Suite Outlook puts AI at the center of CEO accountability. Not at the edges. Not in a pilot. At the center. <a href="https://www.conference-board.org/research/policy-backgrounders/ai-and-the-c-suite-implications-for-ceo-strategy-in-2026" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline">The Conference Board</a></li>
              </ul>

              <div className="pt-8 border-t border-[#E2E8F0] mt-8">
                <p className="text-[#475569] text-[0.9375rem] leading-[1.8] italic mb-6">The best part of writing this isn't the writing. It's the replies. If something here connects to what you're working through this quarter, tell us.</p>
                <div className="text-center">
                  <p className="font-serif text-lg text-[#0B1A2B] mb-1">The APIG Founding Partners</p>
                  <p className="text-sm text-[#64748B] mb-3">Forest T. Harper Jr. · Erania Brackett · Dr. R. Sukumar</p>
                  <p className="text-xs text-[#94A3B8]">APIG — The Alliance for Predictive Intelligence & Growth</p>
                  <div className="flex justify-center gap-4 mt-3">
                    <a href="https://www.harpercookconsultingllc.com/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">Harper Cook</a>
                    <a href="https://brackett.partners/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">Brackett</a>
                    <a href="https://osganalytics.com/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">OSG Analytics</a>
                  </div>
                </div>
              </div>
    </>
  );
}

function PharmaInformerBody() {
  return (
    <>
      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        A global healthcare company had a new RA drug. They needed to know whether to launch it, when, and how to position it against entrenched competitors. That's a big bet. The wrong launch sequence, the wrong messaging, the wrong read on physician behavior, and the investment underperforms before it starts.
      </p>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        They came to OSG because the standard playbook wasn't enough. Market sizing, competitive reviews, pricing models, regulatory checklists. All necessary. None of it tells you what a rheumatologist in Germany actually weighs when choosing between your drug and four alternatives. That's a different kind of question.
      </p>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-4">How ASEMAP™ Changed the Forecast</h2>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        The research covered <strong>ten countries</strong> and <strong>more than 1,000 physicians</strong>. ASEMAP™ evaluated 31 clinical outcomes to find the right mix of endpoints. Two methods gave the team what they actually needed: a choice-driver trade-off analysis that answered four questions most forecasting tools skip entirely:
      </p>

      <ol className="space-y-3 pl-5">
        <li className="text-[#334155] text-[0.9375rem] leading-[1.8] list-decimal">What actually drives a physician to pick one RA drug over another?</li>
        <li className="text-[#334155] text-[0.9375rem] leading-[1.8] list-decimal">How do prescribing patterns shift by line of therapy?</li>
        <li className="text-[#334155] text-[0.9375rem] leading-[1.8] list-decimal">How strong is intent to prescribe Drug A relative to existing brands?</li>
        <li className="text-[#334155] text-[0.9375rem] leading-[1.8] list-decimal">What happens to Drug A's position when future competitors enter the market?</li>
      </ol>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">What Physicians Actually Care About</h2>

      <div className="overflow-hidden border border-[#E2E8F0]">
        <table className="w-full">
          <thead>
            <tr className="bg-[#0B1A2B]">
              <th className="text-left text-xs font-medium text-white uppercase tracking-wider px-5 py-3">Choice Driver</th>
              <th className="text-right text-xs font-medium text-white uppercase tracking-wider px-5 py-3">Relative Importance</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Progression of structural damage', 183],
              ['Incidence and severity of serious infections', 159],
              ['Availability of long-term clinical data', 146],
              ['Onset of action / speed of response', 141],
              ['Mechanism of action specificity', 140],
              ['Sustained remission rates', 135],
              ['Impact on physical function', 134],
              ['Patient-reported outcomes', 125],
              ['Dosing convenience', 123],
              ['Overall safety and tolerability', 119],
            ].map(([driver, importance], i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}>
                <td className="px-5 py-3 text-sm text-[#334155]">{driver}</td>
                <td className="px-5 py-3 text-sm text-[#0B1A2B] font-semibold text-right">
                  <div className="flex items-center justify-end gap-3">
                    <div className="w-24 bg-[#E2E8F0] h-2 rounded-full overflow-hidden hidden sm:block">
                      <div className="h-full bg-[#D97706] rounded-full" style={{ width: `${(importance / 183) * 100}%` }} />
                    </div>
                    {importance}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Structural damage progression topped the list. Not efficacy in the abstract. Not cost. Physicians wanted to know: will this drug slow joint destruction? That's the insight that shapes messaging. If you lead with remission rates when doctors care most about structural damage, you're answering the wrong question.
      </p>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">What It Delivered</h2>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        ASEMAP™ patient share estimates came <strong>within a percentage point</strong> of the latest secondary data for an established benchmark drug. That kind of accuracy changes how a launch team plans.
      </p>

      <div className="border-l-2 border-[#D97706] pl-6 space-y-4">
        <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
          The analysis pinpointed the optimal launch date, the efficacy data to lead with, the endpoints to push in regulatory and commercial conversations, and the messaging that would actually land with prescribers. It also showed how much Drug A's perceived value increased when physicians saw monotherapy data.
        </p>
        <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
          The client's forecasting team went from guessing at competitive positioning to building on hard behavioral data. Not a small difference when hundreds of millions in launch investment are on the line.
        </p>
      </div>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-8">OSG 0360™ CXM+</h2>

      <div className="p-6 bg-[#F8FAFC] border border-[#E2E8F0]">
        <p className="text-[#334155] leading-[1.8] text-[0.9375rem] mb-4">
          CXM+ pairs OSG's customer experience platform with ASEMAP™ behavioral science. The point isn't another dashboard. It's telling you what customers will do next and what to change so they do something better.
        </p>
        <h3 className="text-xs font-medium uppercase tracking-wider text-[#D97706] mb-3">What it does</h3>
        <ul className="space-y-2">
          {[
            'Grows revenue and customer lifetime value by predicting what drives retention',
            'Tests product messaging before launch, not after',
            'Spots risk in pricing and innovation decisions before they become expensive',
            'Moves CX from a reporting exercise to something that changes outcomes',
          ].map((b, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D97706] mt-2 flex-shrink-0" />
              <span className="text-[#334155] text-sm leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Most CX leaders will tell you the same thing about NPS: the board likes having a number, but it doesn't tell the team where to improve or what to do differently. CXM+ fixes that. It connects behavioral analytics to experience measurement, so you get actions, not just scores.
      </p>

      <div className="pt-8 border-t border-[#E2E8F0] mt-8">
        <div className="text-center">
          <p className="font-serif text-lg text-[#0B1A2B] mb-1">APIG / OSG Analytics</p>
          <p className="text-sm text-[#64748B] mb-3">The Alliance for Predictive Intelligence & Growth</p>
          <div className="flex justify-center gap-4 mt-3">
            <a href="https://osganalytics.com/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">osganalytics.com</a>
            <a href="https://predictiveintelligencegrowthalliance.com" className="text-xs text-[#D97706] hover:underline">predictiveintelligencegrowthalliance.com</a>
          </div>
        </div>
      </div>
    </>
  );
}

function PatientMindBody() {
  return (
    <>
      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-2">Three Problems Nobody Talks About Enough</h2>

      <div className="space-y-4">
        {[
          'Good medical advice doesn\'t guarantee good patient decisions. Doctors explain. Patients nod. Then nothing happens.',
          'Agreement in the exam room means very little. Patients agree during the visit, walk out, and never fill the prescription. Or they fill it once and stop.',
          'More information won\'t fix this. The industry keeps adding content when the real problem is behavioral. Fear, doubt, cost. Those are the levers.',
        ].map((challenge, i) => (
          <div key={i} className="flex items-start gap-4 p-5 bg-[#F8FAFC] border border-[#E2E8F0]">
            <span className="w-8 h-8 bg-[#0B1A2B] text-[#D97706] font-serif font-semibold rounded-full flex items-center justify-center flex-shrink-0 text-sm">{i + 1}</span>
            <p className="text-[#334155] text-[0.9375rem] leading-[1.8]">{challenge}</p>
          </div>
        ))}
      </div>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">The Kitchen Table Moment</h2>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Here's what the clinical encounter misses: the decision doesn't happen in the doctor's office. It happens later. At home. The patient sits down, maybe with a spouse, maybe alone, and starts weighing what they heard against what they feel. <strong>Fear</strong> of side effects. <strong>Doubt</strong> about whether it's worth trying. <strong>Cost</strong>, and whether insurance will cover it. That's where adherence is won or lost. Not in the consultation. At the kitchen table.
      </p>

      <div className="border-l-2 border-[#D97706] pl-6 py-2">
        <p className="text-[#475569] text-[0.9375rem] leading-[1.8] italic">
          A patient sits down with a family member, looks at the pill bottle or the injection pen, and asks: "Is this really worth it?" That question, in that moment, decides more than any clinical trial ever published.
        </p>
      </div>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">A Conversation with Dr. R. Sukumar</h2>

      <div className="p-6 bg-[#0B1A2B] text-white">
        <div className="flex items-start gap-5">
          <img src={`${API}/resources/newsletter-img-5.jpg`} alt="Dr. R. Sukumar" className="w-20 h-20 object-cover rounded-full flex-shrink-0" loading="lazy" />
          <div>
            <h3 className="font-serif text-lg text-white mb-1">Dr. R. Sukumar</h3>
            <p className="text-xs text-[#D97706] font-medium mb-3">CEO, OSG Analytics</p>
            <p className="text-sm text-[#CBD5E1] leading-relaxed">
              Twenty-five years in the industry. <strong className="text-[#D97706]">Over a million patient insights</strong> analyzed. What Sukumar keeps coming back to is the same question: why do patients who understand their diagnosis still hesitate, delay, or quit? The answer is almost never about information. It's about what happens between the ears when the stakes feel personal.
            </p>
            <a href="https://www.linkedin.com/events/7473829408445804544/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs text-[#D97706] hover:underline">
              Watch the full conversation <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">What the Industry Keeps Getting Wrong</h2>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Talk to people across pharma, payer, and provider organizations and the same frustration surfaces. Patients aren't failing because they lack access to information. If anything, there's too much of it. A newly diagnosed patient can spend an evening scrolling through clinical studies, Reddit threads, influencer videos, and pharma microsites. By morning, they're more confused than when they started.
      </p>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        And the content patients actually consume tends to be the easiest to digest, not the most accurate. Short videos beat long studies. Anecdotes beat data. That's not a character flaw. It's human behavior. The industry can either keep publishing PDFs nobody reads or start meeting patients where the real decisions happen.
      </p>

      <div className="grid grid-cols-2 gap-4 py-4">
        <div className="bg-[#0B1A2B] p-5 text-center">
          <div className="font-serif text-2xl sm:text-3xl font-semibold text-[#D97706] mb-1">1M+</div>
          <div className="text-[10px] sm:text-xs text-[#94A3B8] leading-snug">Patient insights analyzed</div>
        </div>
        <div className="bg-[#0B1A2B] p-5 text-center">
          <div className="font-serif text-2xl sm:text-3xl font-semibold text-[#D97706] mb-1">25 yrs</div>
          <div className="text-[10px] sm:text-xs text-[#94A3B8] leading-snug">Behavioral analytics experience</div>
        </div>
      </div>

      <div className="pt-8 border-t border-[#E2E8F0] mt-8">
        <p className="text-[#475569] text-[0.9375rem] leading-[1.8] italic mb-6">
          The gap between what doctors recommend and what patients do isn't a knowledge problem. It's a behavior problem. Until the industry treats it that way, adherence numbers won't move.
        </p>
        <div className="text-center">
          <p className="font-serif text-lg text-[#0B1A2B] mb-1">APIG / OSG Analytics</p>
          <p className="text-sm text-[#64748B] mb-3">The Alliance for Predictive Intelligence & Growth</p>
          <div className="flex justify-center gap-4 mt-3">
            <a href="https://osganalytics.com/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">osganalytics.com</a>
            <a href="https://predictiveintelligencegrowthalliance.com" className="text-xs text-[#D97706] hover:underline">predictiveintelligencegrowthalliance.com</a>
          </div>
        </div>
      </div>
    </>
  );
}

function BreakthroughMentoringBody() {
  return (
    <>
      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Most leadership programs teach strategy, finance, execution. Important things. But ask the executives who actually changed an organization and they'll point to something simpler: someone who showed them how to lead before they had the title.
      </p>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Forest T. Harper Jr. has spent decades in rooms where influence determines outcomes. Twenty-eight years at Pfizer. CEO of INROADS. U.S. Army Captain. Across all of it, one pattern held: the leaders with the deepest impact weren't the loudest strategists. They were the ones who invested in making other people better.
      </p>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-4">Watch: Breakthrough Mentoring</h2>

      <div className="w-full aspect-video bg-[#0B1A2B] overflow-hidden" data-testid="video-embed">
        <iframe
          src="https://player.vimeo.com/video/1210251665?h=0&title=0&byline=0&portrait=0"
          className="w-full h-full"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title="Breakthrough Mentoring with Forest T. Harper Jr."
        />
      </div>

      <p className="text-xs text-[#94A3B8] mt-2">
        Forest T. Harper Jr., Harper & Cook Consulting. Designed to provide insights on leading others and communicating to influence.
      </p>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">Why This Matters Now</h2>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Influence is the essence of leadership. Not authority. Not a title on a door. The ability to change how someone thinks, what they prioritize, and whether they act. When it's done well, the impact compounds for years. When it's missing, organizations stall no matter how good the strategy looks on paper.
      </p>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        In this conversation, Harper lays out what separates mentoring that actually builds leaders from the kind that checks a box. It's direct. It's drawn from real experience. And it connects to a question every senior leader eventually faces: are you building capacity that outlasts you, or just running a team?
      </p>

      <div className="border-l-2 border-[#D97706] pl-6 space-y-4">
        <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
          <strong>On communication:</strong> Influence starts with how you speak to people. Not presenting. Speaking. There's a difference. One fills a room. The other moves someone to act.
        </p>
        <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
          <strong>On mentoring vs. managing:</strong> A manager tells you what to do this quarter. A mentor changes what you believe you're capable of. Both matter. Only one of them lasts.
        </p>
        <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
          <strong>On legacy:</strong> The organizations that endure aren't the ones with the best plans. They're the ones where leaders made more leaders. That's the multiplier nobody puts in a spreadsheet.
        </p>
      </div>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">About Forest T. Harper Jr.</h2>

      <div className="p-6 bg-[#0B1A2B] text-white">
        <div className="flex items-start gap-5">
          <img src={`${API}/resources/newsletter-img-3.jpg`} alt="Forest T. Harper Jr." className="w-20 h-20 object-cover rounded-full flex-shrink-0" loading="lazy" />
          <div>
            <h3 className="font-serif text-lg text-white mb-1">Forest T. Harper Jr.</h3>
            <p className="text-xs text-[#D97706] font-medium mb-3">Founder & President, Harper Cook Consulting</p>
            <p className="text-sm text-[#CBD5E1] leading-relaxed">
              28 years in Pfizer's C-suite. Former CEO of INROADS. U.S. Army Captain. ELC NACD certified board director. Harper connects validated strategies to investment capital, strategic partners, and the kind of C-suite leadership expertise that turns plans into funded execution.
            </p>
            <a href="https://harpercookconsultingllc.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs text-[#D97706] hover:underline">
              harpercookconsultingllc.com <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      <div className="pt-8 border-t border-[#E2E8F0] mt-8">
        <p className="text-[#475569] text-[0.9375rem] leading-[1.8] italic mb-6">
          Leadership development isn't a program. It's a practice. If this conversation sparks something you want to explore with your own team, reach out.
        </p>
        <div className="text-center">
          <p className="font-serif text-lg text-[#0B1A2B] mb-1">APIG / Harper Cook Consulting</p>
          <p className="text-sm text-[#64748B] mb-3">The Alliance for Predictive Intelligence & Growth</p>
          <div className="flex justify-center gap-4 mt-3">
            <a href="https://harpercookconsultingllc.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">harpercookconsultingllc.com</a>
            <a href="https://predictiveintelligencegrowthalliance.com" className="text-xs text-[#D97706] hover:underline">predictiveintelligencegrowthalliance.com</a>
          </div>
        </div>
      </div>
    </>
  );
}

function StrategicIntelligenceBody() {
  return (
    <>
      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Every executive says they want better data. Fewer admit what they actually need: better judgment about what the data means. That's the gap Erania Brackett has spent her career closing.
      </p>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        In this interview, she walks through how strategic intelligence works in practice. Not as a slide deck exercise. As a way of making decisions when the market is shifting, the board wants answers, and the usual playbook isn't enough.
      </p>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-4">Watch the Full Interview</h2>

      <div className="w-full bg-[#0B1A2B] p-8 sm:p-12 text-center" data-testid="video-link-block">
        <p className="text-[#CBD5E1] text-sm mb-4">Full video interview hosted by XRaised</p>
        <a
          href="https://xraised.com/videos/strategic-intelligence-for-leaders/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#D97706] text-white px-8 py-3 text-sm font-medium tracking-wide hover:bg-[#B45309] transition-colors"
          data-testid="watch-video-btn"
        >
          Watch: Strategic Intelligence for Leaders
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">What She Covers</h2>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Brackett's background spans strategy, marketing, and AI. She ran P&Ls at the Fortune 1000 level, sat on a NYSE board, and built Brackett Partners to do what most advisory firms talk about but rarely deliver: connect predictive intelligence to decisions that actually move revenue.
      </p>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        In the conversation, she covers three things that keep coming up in boardrooms right now:
      </p>

      <div className="border-l-2 border-[#D97706] pl-6 space-y-4">
        <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
          <strong>How to turn market noise into signal.</strong> There's no shortage of data. The problem is knowing which data points matter for your specific decision, this quarter, with your competitive set. Brackett's approach starts with the decision, not the dashboard.
        </p>
        <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
          <strong>Why execution fails when intelligence stays in a deck.</strong> Insight without a plan to act on it is expensive decoration. She talks about what it takes to bridge the gap between "we know what to do" and "it's actually happening across the organization."
        </p>
        <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
          <strong>Where AI fits and where it doesn't.</strong> Not everything needs a model. But the decisions that do, the ones with real capital at stake, need intelligence that's been validated against real customer behavior. That's where predictive analytics earns its keep.
        </p>
      </div>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">Who Should Watch This</h2>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        If you're an executive navigating a market disruption, evaluating an innovation strategy, or trying to figure out whether your current positioning still holds, this is 20 minutes well spent. Brackett doesn't deal in abstractions. She talks about what she's seen work and what she's seen fail, and the difference between the two usually comes down to discipline, not brilliance.
      </p>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">About Erania Brackett</h2>

      <div className="p-6 bg-[#0B1A2B] text-white">
        <div className="flex items-start gap-5">
          <img src="https://customer-assets.emergentagent.com/job_three-superpowers/artifacts/pvn6eazu_Erania%20Head%20Shot_best.jpg" alt="Erania Brackett" className="w-20 h-20 object-cover rounded-full flex-shrink-0" style={{ objectPosition: 'center 20%' }} loading="lazy" />
          <div>
            <h3 className="font-serif text-lg text-white mb-1">Erania Brackett</h3>
            <p className="text-xs text-[#D97706] font-medium mb-3">Founder & CEO, Brackett Partners</p>
            <p className="text-sm text-[#CBD5E1] leading-relaxed">
              Former Fortune 1000 C-suite executive with P&L leadership across global marketing, customer experience, and ESG. Former board director (NYSE: KN). Brackett Partners delivers customer-validated strategy, AI-powered predictive intelligence, and sell-side M&A advisory through Britehorn Securities.
            </p>
            <div className="flex gap-4 mt-3">
              <a href="https://brackett.partners" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">brackett.partners</a>
              <a href="https://www.linkedin.com/in/erania-brackett/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">LinkedIn</a>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-8 border-t border-[#E2E8F0] mt-8">
        <p className="text-[#475569] text-[0.9375rem] leading-[1.8] italic mb-6">
          The leaders who win aren't the ones with the most information. They're the ones who know what to do with it. If that distinction matters to you, this interview is worth your time.
        </p>
        <div className="text-center">
          <p className="font-serif text-lg text-[#0B1A2B] mb-1">APIG / Brackett Partners</p>
          <p className="text-sm text-[#64748B] mb-3">The Alliance for Predictive Intelligence & Growth</p>
          <div className="flex justify-center gap-4 mt-3">
            <a href="https://brackett.partners" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">brackett.partners</a>
            <a href="https://predictiveintelligencegrowthalliance.com" className="text-xs text-[#D97706] hover:underline">predictiveintelligencegrowthalliance.com</a>
          </div>
        </div>
      </div>
    </>
  );
}

function AboveCloudsBody() {
  return (
    <>
      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Everyone's rushing to plug AI into everything. Workflows, customer service, product development, forecasting. The assumption is that the tool itself is the advantage. It isn't.
      </p>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Most AI tools pull from the same public data sets. The same research. The same scraped content. If your competitor is using the same model on the same data, you're both going to arrive at the same answer. That's not a competitive edge. That's a coin flip with extra steps.
      </p>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-4">Your AI Is Only as Good as Its Data</h2>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        The companies that will actually win with AI aren't the ones that adopted it fastest. They're the ones that feed it something nobody else has. Proprietary customer data. Behavioral patterns from their own markets. Real signals from real interactions, not recycled industry benchmarks.
      </p>

      <p className="text-[#334155] leading-[1.8] text-[0.9375rem]">
        Without unique data, AI doesn't differentiate you. It homogenizes you. And a homogenized strategy in a crowded market is just expensive mediocrity.
      </p>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-4">Notable Perspectives</h2>

      <div className="space-y-6">
        <div className="border-l-2 border-[#D97706] pl-6">
          <p className="text-[#334155] leading-[1.8] text-[0.9375rem] italic">
            "If you want hot tea and someone else wants cold tea, traditional models give you lukewarm tea. The market doesn't want lukewarm tea."
          </p>
          <p className="text-sm text-[#64748B] mt-2 font-medium">— Dr. R. Sukumar</p>
        </div>

        <div className="border-l-2 border-[#D97706] pl-6">
          <p className="text-[#334155] leading-[1.8] text-[0.9375rem] italic">
            "Is this a regular problem, or is this a bleeding-from-the-neck problem? Customers will pay immediately to fix a bleeding-from-the-neck problem."
          </p>
          <p className="text-sm text-[#64748B] mt-2 font-medium">— Richard</p>
        </div>
      </div>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">A Conversation with Dr. R. Sukumar</h2>

      <div className="p-6 bg-[#0B1A2B] text-white">
        <div className="flex items-start gap-5">
          <img src={`${API}/resources/newsletter-img-5.jpg`} alt="Dr. R. Sukumar" className="w-20 h-20 object-cover rounded-full flex-shrink-0" loading="lazy" />
          <div>
            <h3 className="font-serif text-lg text-white mb-1">Dr. R. Sukumar</h3>
            <p className="text-xs text-[#D97706] font-medium mb-3">CEO, OSG Analytics</p>
            <p className="text-sm text-[#CBD5E1] leading-relaxed">
              Twenty-five years in the industry. Over a million patient insights analyzed. In this episode, Sukumar breaks down why AI without proprietary data is just expensive guessing, and what it actually takes to understand what customers will do next.
            </p>
            <a href="https://www.youtube.com/watch?v=u060BSYZ-OA" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs text-[#D97706] hover:underline">
              Watch the full episode on YouTube <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      <h2 className="font-serif text-2xl text-[#0B1A2B] pt-6">Key Takeaways</h2>

      <div className="space-y-4">
        {[
          { title: 'The Pitfall of Averages', text: 'When you average out what different customers want, you end up with a product nobody actually wants. Hot tea drinkers and cold tea drinkers don\'t want lukewarm tea. The same principle applies to strategy, product design, and market positioning. Personalization isn\'t a nice-to-have. It\'s the difference between relevance and irrelevance.' },
          { title: 'The Boundaries of AI', text: 'AI can automate tasks and surface patterns. It cannot replace the human judgment needed for strategic decisions, product development, or understanding why a customer hesitates. The companies treating AI as a replacement for thinking are the ones that will get blindsided.' },
          { title: 'The Evolution of Data', text: 'Data isn\'t just numbers anymore. Sentiment, emotion, cultural context, behavioral patterns. The organizations that integrate qualitative signals alongside quantitative metrics are building a picture their competitors can\'t see.' },
          { title: 'The Danger of Channel Saturation', text: 'More marketing channels doesn\'t mean more reach. It often means more noise. When every channel is flooded, customers disengage. The winning play is fewer, better-targeted messages that actually land.' },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-4 p-5 bg-[#F8FAFC] border border-[#E2E8F0]">
            <span className="w-8 h-8 bg-[#0B1A2B] text-[#D97706] font-serif font-semibold rounded-full flex items-center justify-center flex-shrink-0 text-sm">{i + 1}</span>
            <div>
              <h3 className="font-medium text-[#0B1A2B] mb-1">{item.title}</h3>
              <p className="text-[#475569] text-sm leading-relaxed">{item.text}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-8 border-t border-[#E2E8F0] mt-8">
        <p className="text-[#475569] text-[0.9375rem] leading-[1.8] italic mb-6">
          The AI race isn't about who adopts fastest. It's about who feeds the machine something worth learning from. If your data looks like everyone else's, your output will too.
        </p>
        <div className="text-center">
          <p className="font-serif text-lg text-[#0B1A2B] mb-1">APIG / OSG Analytics</p>
          <p className="text-sm text-[#64748B] mb-3">The Alliance for Predictive Intelligence & Growth</p>
          <div className="flex justify-center gap-4 mt-3">
            <a href="https://osganalytics.com/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#D97706] hover:underline">osganalytics.com</a>
            <a href="https://predictiveintelligencegrowthalliance.com" className="text-xs text-[#D97706] hover:underline">predictiveintelligencegrowthalliance.com</a>
          </div>
        </div>
      </div>
    </>
  );
}

function NewsletterIndex() {
  return (
    <>
      <Helmet>
        <title>The APIG Brief — Newsletter | APIG Alliance</title>
        <meta name="description" content="The APIG Brief: a monthly signal for leaders navigating capital, intelligence, and execution. Insights from The Alliance for Predictive Intelligence & Growth." />
        <link rel="canonical" href="https://predictiveintelligencegrowthalliance.com/newsletter" />
      </Helmet>

      <section className="pt-32 pb-16 bg-white">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="overline mb-4">The APIG Brief</p>
            <h1 className="heading-display text-4xl sm:text-5xl text-[#0B1A2B] mb-6" data-testid="newsletter-headline">
              Intelligence That Arrives Before the Meeting.
            </h1>
            <p className="body-large">
              A monthly signal for leaders navigating capital, intelligence, and execution. Fewer trend headlines, more perspective you can actually use.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto space-y-8">
            {articles.map((article) => (
              <a
                key={article.slug}
                href={`/newsletter/${article.slug}`}
                className="block group border border-[#E2E8F0] hover:border-[#D97706]/30 hover:shadow-lg transition-all duration-500"
                data-testid={`newsletter-card-${article.slug}`}
              >
                <div className="md:flex">
                  <div className="md:w-2/5">
                    <img src={article.heroImage} alt={article.heroAlt} className="w-full h-48 md:h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-6 md:p-8 md:w-3/5 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium text-[#D97706] uppercase tracking-wider">{article.volume} · {article.issue}</span>
                      <span className="text-[#CBD5E1]">|</span>
                      <span className="text-xs text-[#64748B]">{article.date}</span>
                    </div>
                    <h2 className="font-serif text-xl lg:text-2xl text-[#0B1A2B] mb-3 leading-snug group-hover:text-[#D97706] transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-[#475569] leading-relaxed">
                      {article.subtitle}
                    </p>
                    <span className="inline-flex items-center text-sm font-medium text-[#0B1A2B] group-hover:text-[#D97706] mt-4 transition-colors">
                      Read the Brief
                      <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default function NewsletterPage() {
  const path = window.location.pathname;
  const slug = path.replace('/newsletter/', '').replace('/newsletter', '');
  const article = slug ? articles.find(a => a.slug === slug) : null;

  return (
    <div className="min-h-screen bg-white" data-testid="newsletter-page">
      <Navigation />

      {article ? <ArticlePage article={article} /> : <NewsletterIndex />}

      <Footer />
    </div>
  );
}
